// HelpArticles.js

export const HELP_APP_TYPE_KEY = 'APP';
export const HELP_ARTICLE_TITLE_MAX = 200;
export const HELP_ARTICLE_TAG_MAX = 20;
export const HELP_ARTICLE_TYPE_MAX = 20;
export const HELP_IMAGE_MAX_BYTES = 8 * 1024 * 1024;
export const HELP_ICON_PATH = 'M478-240q21 0 35.5-14.5T528-290q0-21-14.5-35.5T478-340q-21 0-35.5 14.5T428-290q0 21 14.5 35.5T478-240Zm-36-154h74q0-33 7.5-52t42.5-52q26-26 41-49.5t15-56.5q0-56-41-86t-97-30q-57 0-92.5 30.5T342-618l66 26q5-18 22.5-39t53.5-21q32 0 48 17.5t16 38.5q0 20-12 37.5T506-526q-44 39-54 59t-10 73Zm38 314q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z';

/**
 * Normalizes a string for storage and comparison.
 *
 * @param {*} value - Raw value.
 * @returns {string}
 */
export function normalizeHelpString(value) {
  return String(value || '').trim();
}

/**
 * Returns the canonical type key used as help context, or empty when none.
 *
 * @param {*} typeKey - Type key or empty/App scope.
 * @returns {string}
 */
export function normalizeHelpTypeKey(typeKey) {
  const cleaned = normalizeHelpString(typeKey).toUpperCase();
  if (!cleaned || cleaned === HELP_APP_TYPE_KEY) return '';
  return cleaned;
}

/**
 * Returns whether a type key is the reserved unscoped / App-wide value.
 *
 * @param {*} typeKey - Type key or scope.
 * @returns {boolean}
 */
export function isHelpAppTypeKey(typeKey) {
  return !normalizeHelpTypeKey(typeKey);
}

/**
 * Returns the canonical type key used on legacy help article documents.
 *
 * @param {*} typeKey - Type key or empty/App scope.
 * @returns {string}
 */
export function resolveHelpArticleTypeKey(typeKey) {
  return normalizeHelpTypeKey(typeKey) || HELP_APP_TYPE_KEY;
}

/**
 * Parses type context from an array or a legacy single type key.
 *
 * @param {*} value - Raw typeKeys array.
 * @param {*} [legacyTypeKey] - Older single typeKey field.
 * @returns {string[]}
 */
export function parseHelpArticleTypeKeys(value, legacyTypeKey) {
  const rawItems = Array.isArray(value) ? value : [];
  const seen = new Set();
  const typeKeys = [];

  rawItems.forEach((item) => {
    const typeKey = normalizeHelpTypeKey(item);
    if (!typeKey || seen.has(typeKey)) return;
    seen.add(typeKey);
    typeKeys.push(typeKey);
  });

  if (typeKeys.length === 0) {
    const fallback = normalizeHelpTypeKey(legacyTypeKey);
    if (fallback) typeKeys.push(fallback);
  }

  return typeKeys.slice(0, HELP_ARTICLE_TYPE_MAX);
}

/**
 * Returns the type context on an article.
 *
 * @param {object} article
 * @returns {string[]}
 */
export function getHelpArticleTypeKeys(article) {
  return parseHelpArticleTypeKeys(article?.typeKeys, article?.typeKey);
}

/**
 * Returns whether an article has no type context.
 *
 * @param {object} article
 * @returns {boolean}
 */
export function isHelpArticleUnscoped(article) {
  return getHelpArticleTypeKeys(article).length === 0;
}

/**
 * Returns whether an article applies to a type.
 *
 * @param {object} article
 * @param {*} typeKey
 * @returns {boolean}
 */
export function helpArticleHasType(article, typeKey) {
  const cleaned = normalizeHelpTypeKey(typeKey);
  if (!cleaned) return isHelpArticleUnscoped(article);
  return getHelpArticleTypeKeys(article).includes(cleaned);
}

/**
 * Resolves the current reader type from a node or collection.
 *
 * @param {object|null} [node]
 * @param {object|null} [database]
 * @returns {string}
 */
export function resolveHelpContextTypeKey(node, database) {
  return normalizeHelpTypeKey(
    node?.type || database?.typeKey || database?.type || '',
  );
}

/**
 * Parses a comma-separated tag string into a unique list.
 *
 * @param {*} value - Raw tags string or array.
 * @returns {string[]}
 */
export function parseHelpArticleTags(value) {
  const rawItems = Array.isArray(value)
    ? value
    : String(value || '').split(',');

  const seen = new Set();
  const tags = [];

  rawItems.forEach((item) => {
    const tag = normalizeHelpString(item);
    if (!tag) return;
    const key = tag.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    tags.push(tag);
  });

  return tags.slice(0, HELP_ARTICLE_TAG_MAX);
}

/**
 * Formats tags for an input field.
 *
 * @param {string[]} tags - Stored tags.
 * @returns {string}
 */
export function formatHelpArticleTags(tags) {
  return (Array.isArray(tags) ? tags : []).join(', ');
}

/**
 * Sorts pinned articles first, then by position and title.
 *
 * @param {object} left - Left article.
 * @param {object} right - Right article.
 * @returns {number}
 */
export function compareHelpArticles(left, right) {
  const leftPinned = left?.pinned === true;
  const rightPinned = right?.pinned === true;
  if (leftPinned !== rightPinned) return leftPinned ? -1 : 1;

  const leftPosition = Number(left?.position);
  const rightPosition = Number(right?.position);
  const safeLeft = Number.isFinite(leftPosition) ? leftPosition : 0;
  const safeRight = Number.isFinite(rightPosition) ? rightPosition : 0;
  if (safeLeft !== safeRight) return safeLeft - safeRight;

  return normalizeHelpString(left?.title).localeCompare(
    normalizeHelpString(right?.title),
    undefined,
    { sensitivity: 'base' },
  );
}

/**
 * Returns the next position after the highest existing article.
 *
 * @param {object[]} articles - Existing articles.
 * @returns {number}
 */
export function getNextHelpArticlePosition(articles) {
  const rows = Array.isArray(articles) ? articles : [];
  return rows.reduce((max, row) => {
    const position = Number(row?.position);
    if (!Number.isFinite(position)) return max;
    return Math.max(max, position);
  }, 0) + 1;
}

/**
 * Returns whether an article matches a search query.
 *
 * @param {object} article - Article record.
 * @param {*} query - Search text.
 * @returns {boolean}
 */
export function helpArticleMatchesQuery(article, query) {
  const needle = normalizeHelpString(query).toLowerCase();
  if (!needle) return true;

  const tags = Array.isArray(article?.tags) ? article.tags : [];
  const typeKeys = getHelpArticleTypeKeys(article);
  const haystack = [
    article?.title,
    article?.contentText,
    ...typeKeys,
    ...tags,
  ]
    .map((part) => normalizeHelpString(part).toLowerCase())
    .join(' ');

  return haystack.includes(needle);
}

/**
 * Filters and sorts articles for the reader or admin list.
 *
 * @param {object[]} articles - Candidate articles.
 * @param {object} [options]
 * @param {string} [options.query]
 * @param {string} [options.typeKey]
 * @param {boolean} [options.scopedOnly]
 * @param {string} [options.typeFilter]
 * @returns {object[]}
 */
export function filterHelpArticles(articles, options = {}) {
  const rows = Array.isArray(articles) ? articles : [];
  const query = normalizeHelpString(options.query);
  const typeKey = normalizeHelpTypeKey(options.typeKey);
  const typeFilter = normalizeHelpString(options.typeFilter).toUpperCase();
  const scopedOnly = options.scopedOnly === true && !query && !typeFilter;

  return rows
    .filter((article) => {
      if (typeFilter === 'NONE') {
        if (!isHelpArticleUnscoped(article)) return false;
      } else if (typeFilter && typeFilter !== 'ALL') {
        if (!helpArticleHasType(article, typeFilter)) return false;
      } else if (scopedOnly && typeKey) {
        if (!helpArticleHasType(article, typeKey) && !isHelpArticleUnscoped(article)) {
          return false;
        }
      }
      return helpArticleMatchesQuery(article, query);
    })
    .slice()
    .sort((left, right) => {
      if (!query && typeKey) {
        const leftMatch = helpArticleHasType(left, typeKey);
        const rightMatch = helpArticleHasType(right, typeKey);
        if (leftMatch !== rightMatch) return leftMatch ? -1 : 1;
      }
      return compareHelpArticles(left, right);
    });
}
